import React from "react";
import Radium from "radium";
import { CircularProgress } from "material-ui";
import Sticky from "react-stickynode";
import clone from "lodash/clone";
import sortBy from "lodash/sortBy";
import throttle from "lodash/throttle";
import Fuse from "fuse.js";

import { DETAILS, CREDITS } from "../../film.jsx";

import * as util from "../../../util";

import FilmListItem from "./film-list-item.jsx";
import FilmFilterEditor from "./film-filter-editor.jsx";

const CONTAINER_STYLE = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  margin: "1.5em 0 -1.5em",
  WebkitTransform: "translate3d(0,0,0)",
  width: "100%",
  paddingBottom: "3em",
  "@media (max-width: 660px)": {
    margin: "2.5em 0 -1.5em"
  }
};

const POSTER_STYLE = {
  maxWidth: "50vw",
  width: 200,
  boxSizing: "border-box",
  "@media (min-width: 661px)": {
    margin: "1em"
  }
};

const STILLS_STYLE = {
  maxWidth: "auto",
  margin: "1em",
  width: 275
};

const NO_FILMS_STYLE = {
  fontStyle: "italic",
  fontSize: "1.5em",
  margin: "2em",
  textAlign: "center"
};

const LIST_STYLE = {
  width: "100%",
  margin: "1em 3em"
};

const DISPLAY_SECTION_TITLE_STYLE = {
  fontWeight: "bold",
  textAlign: "center",
  margin: "2em 0 1em",
  fontSize: "2em"
};

class FilmList extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      filterSticky: false,
      scrollPosition: 0,
      bottomBoundary: 0
    };

    this._onSearchChange = this._onSearchChange.bind(this);
    this._onModeChange = this._onModeChange.bind(this);
    this._onTagChange = this._onTagChange.bind(this);
    this._onDisplaySectionChange = this._onDisplaySectionChange.bind(this);
    this._updateScroll = throttle(this._updateScroll.bind(this), 100);
    this._onStateChange = this._onStateChange.bind(this);
    this._onStarredChange = this._onStarredChange.bind(this);
  }

  shouldComponentUpdate() {
    return util.shouldComponentUpdateDeep.apply(this, arguments);
  }

  static contextTypes = {
    isLarge: React.PropTypes.bool,
    tenant: React.PropTypes.object
  };

  componentDidMount() {
    this.componentWillReceiveProps(this.props);
    window.addEventListener("scroll", this._updateScroll);
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this._updateScroll);
  }

  componentWillReceiveProps(newProps) {
    return;
  }

  _updateScroll() {
    if (this._pendingScrollUpdate) {
      return;
    }
    this._pendingScrollUpdate = true;

    if (window.scrollY > this.state.scrollPosition) {
      let rect =
        this._editor &&
        this._editor.getBoundingClientRect &&
        this._editor.getBoundingClientRect();
      this.setState(
        {
          hasScrolledUp: false,
          scrollPosition: window.scrollY,
          bottomBoundary: this.state.hasScrolledUp
            ? rect
              ? rect.bottom + window.scrollY
              : window.scrollY
            : this.state.bottomBoundary
        },
        () => {
          this._pendingScrollUpdate = false;
        }
      );
    } else if (window.scrollY < this.state.scrollPosition) {
      this.setState(
        {
          hasScrolledUp: true,
          scrollPosition: window.scrollY,
          bottomBoundary: this.state.hasScrolledUp
            ? this.state.bottomBoundary
            : window.scrollY
        },
        () => {
          this._pendingScrollUpdate = false;
        }
      );
    }
  }

  _onStateChange(state) {
    this.props.onStateChange(state);
    if (this._container && this._container.getBoundingClientRect) {
      window.scrollTo(
        0,
        this._container.getBoundingClientRect().top + window.scrollY - 64 - 24
      );
    }
  }

  _onSearchChange(search) {
    this._onStateChange({ search });
  }

  _onModeChange(mode) {
    this._onStateChange({ mode: mode === "grid" ? undefined : mode });
  }

  _onTagChange(tag, enable, stateOptions) {
    let tags = clone(this.props.filterTags);
    if (enable) {
      tags[tag] = true;
    } else {
      delete tags[tag];
    }
    this._onStateChange({ filterTags: tags }, stateOptions);
  }

  _onDisplaySectionChange(tagId, stateOptions) {
    this._onStateChange(
      { filterTags: tagId ? { [tagId]: true } : {} },
      stateOptions
    );
  }

  _onStarredChange() {
    // Don't auto-scroll for starred since it may reduce the list size
    this.props.onStateChange({ starred: !this.props.starred });
  }

  render() {
    let { isLarge } = this.context,
      {
        hideFilterEditor,
        showLoadingIndicator,
        films,
        filterTags,
        mode,
        search,
        starred,
        wishlistFilms
      } = this.props;

    let forcedListMode = false;

    // Films (strip unnamed ones)
    films = Object.keys(films)
      .filter(f => {
        return (
          films[f].name &&
          films[f].name !== "Unnamed Film" &&
          films[f].visibility !== "hidden" &&
          films[f].visibility !== "hidden_everywhere"
        );
      })
      .reduce(function (obj, f) {
        obj[f] = films[f];
        return obj;
      }, {});

    // Tags & venues
    let tags = {};
    Object.keys(films).forEach(function (k) {
      films[k].tags.forEach(t => {
        tags[t.id] = { id: t.id, name: t.name, color: t.color, visible: t.visible };
      });
    });
    let tagList = sortBy(Object.values(tags), "name");

    let sortedFilms = Object.values(films).sort((a, b) =>
      a.sort_key.localeCompare(b.sort_key)
    );

    let filteredFilms = sortedFilms.filter(f => {
      if (f.type === "livestream") {
        return false;
      }

      if (
        Object.keys(filterTags).length &&
        !f.tags.some(t => filterTags[t.id])
      ) {
        return false;
      }

      if (starred && wishlistFilms.indexOf(f.id) === -1) {
        return false;
      }

      return true;
    });

    // text search - dive into full text search
    if (search) {
      mode = "list";
      forcedListMode = true;
      let fuse = new Fuse(filteredFilms, {
        shouldSort: true,
        threshold: 0.6,
        location: 0,
        distance: 1000,
        maxPatternLength: 32,
        minMatchCharLength: 2,
        includeMatches: true,
        keys: [
          { name: "name", weight: 0.7 },
          { name: "short_description", weight: 0.3 },
          { name: "description", weight: 0.3 }
        ].concat(
          Object.keys(DETAILS)
            .concat(Object.keys(CREDITS))
            .map(k => {
              return {
                name: `${DETAILS[k] ? "details" : "credits"}.${k}`,
                weight: 0.1
              };
            })
        )
      });
      filteredFilms = fuse
        .search(search.trim())
        .slice(0, 10)
        .map(r => {
          // just top 10 results
          // [Match highlighting currently disabled -- distracting]
          r.item._search_match_details = r.matches;
          return r.item;
        });
    } else {
      filteredFilms.forEach(f => {
        delete f._search_match_details;
      });
    }

    const useDisplaySectionMode =
      !!this.context.tenant.film_guide_enable_display_sections &&
      mode === "grid";

    return (
      <div>
        {!showLoadingIndicator ? (
          <div
            ref={r => {
              this._container = r;
            }}
          >
            {!hideFilterEditor ? (
              <Sticky
                enableTransforms={false}
                top={64 + 26}
                bottomBoundary={this.state.bottomBoundary || 1}
                innerZ={999}
                onStateChange={s => this.setState({ filterSticky: s === 2 })}
              >
                <div
                  ref={r => {
                    this._editor = r;
                  }}
                >
                  <FilmFilterEditor
                    displaySectionMode={useDisplaySectionMode}
                    tags={
                      useDisplaySectionMode
                        ? (
                            this.context.tenant
                              .film_guide_selected_display_sections || []
                          )
                            .map(tagId => tagList.find(t => t.id === tagId))
                            .filter(x => !!x)
                        : tagList
                    }
                    isSticky={this.state.filterSticky}
                    search={search}
                    onSearchChange={this._onSearchChange}
                    mode={mode}
                    forcedListMode={forcedListMode}
                    onModeChange={this._onModeChange}
                    filterTags={filterTags}
                    onTagChange={this._onTagChange}
                    onDisplaySectionChange={this._onDisplaySectionChange}
                    starred={starred}
                    onStarredChange={this._onStarredChange}
                  />
                </div>
              </Sticky>
            ) : null}
            {(useDisplaySectionMode && filteredFilms.length
              ? this.context.tenant.film_guide_selected_display_sections.filter(
                  tagId => {
                    if (Object.keys(filterTags).length) {
                      return !!filterTags[tagId];
                    }
                    return true;
                  }
                ) || []
              : ["__DEFAULT__"]
            ).map(section => {
              const isDisplaySection = section !== "__DEFAULT__";
              const films = isDisplaySection
                ? filteredFilms.filter(f => f.tags.some(t => t.id === section))
                : filteredFilms;

              if (isDisplaySection && films.length === 0) {
                return null;
              }

              const inner = (
                <div style={CONTAINER_STYLE} className="Films--film_container">
                  {films.map(f => {
                    return (
                      <FilmListItem
                        key={f.id}
                        film={f}
                        style={
                          mode === "grid"
                            ? Object.assign(
                                {},
                                POSTER_STYLE,
                                this.context.tenant.film_guide_stills_mode &&
                                  STILLS_STYLE
                              )
                            : LIST_STYLE
                        }
                        showInner={!!this.props.search}
                        expanded={mode === "list"}
                        onTagChange={this._onTagChange}
                        showWishlistButton={isLarge}
                        isWishlisted={wishlistFilms.indexOf(f.id) !== -1}
                      />
                    );
                  })}
                  {sortedFilms.length === 0 &&
                  this.context.tenant.marquee_enabled ? (
                    <div style={NO_FILMS_STYLE}>
                      No films are currently showing.
                    </div>
                  ) : films.length === 0 && starred ? (
                    <div style={NO_FILMS_STYLE}>
                      No starred films found. Try starring films by clicking the
                      Star button on films you're interested in.
                    </div>
                  ) : films.length === 0 ? (
                    <div style={NO_FILMS_STYLE}>
                      No films found.
                      Try&nbsp;adjusting&nbsp;your&nbsp;search&nbsp;terms.
                    </div>
                  ) : null}
                </div>
              );

              if (isDisplaySection) {
                const tag = films[0].tags.find(t => t.id === section);

                return (
                  <div key={section}>
                    <h2 style={DISPLAY_SECTION_TITLE_STYLE}>{tag.name}</h2>
                    {inner}
                  </div>
                );
              } else {
                return inner;
              }
            })}
          </div>
        ) : (
          <div style={{ padding: 10, textAlign: "center" }}>
            <CircularProgress
              mode="indeterminate"
              size={119}
              thickness={7}
              style={{ margin: 10.5 }}
            />
          </div>
        )}
      </div>
    );
  }
}

FilmList.contextTypes = {
  tenant: React.PropTypes.object,
  isLarge: React.PropTypes.bool
};

export default Radium(FilmList);



// WEBPACK FOOTER //
// ./src/app/components/partials/film-list/film-list.jsx